import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { logout } from '../../store/slices/authSlice';
import TokenStatus from '../common/TokenStatus';
import {
  HomeIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  XMarkIcon,
  ArrowLeftOnRectangleIcon,
  ShieldCheckIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isDarkMode: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, isDarkMode }) => {
  const { user } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Organizations', href: '/organizations', icon: BuildingOfficeIcon },
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Users', href: '/users', icon: UsersIcon },
    ...(user?.role === 'super_admin' || user?.role === 'org_admin' ? [
      { name: 'Usage Stats', href: '/usage-stats', icon: ChartBarIcon },
      { name: 'Audit Logs', href: '/audit-logs', icon: ClipboardDocumentListIcon },
    ] : []),
    ...(user?.role === 'super_admin' ? [
      { name: 'Billing', href: '/billing', icon: CreditCardIcon },
      { name: 'Limits', href: '/limits', icon: ShieldCheckIcon }
    ] : []),
    { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
  ];

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        className={`hidden md:flex md:flex-col fixed top-8 bottom-0 left-0 w-64 bg-light-surface dark:bg-dark-surface border-r border-light-border dark:border-dark-border transform transition-all duration-200 ease-in-out z-40 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section */}
          <div className="p-6 border-b border-light-border dark:border-dark-border">
            <div className="flex items-center gap-3">
              <img 
                src="/images/ana_logo.png" 
                alt="ANA Logo" 
                className="h-8 w-auto"
              />
              <span className="text-xl font-bold text-gray-900 dark:text-white">
                Admin Panel
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || 
                             (item.href !== '/' && location.pathname.startsWith(item.href));
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-4 py-3 mb-2 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-primary-500/10 dark:bg-primary-500/20 text-primary-500 dark:text-primary-400 border border-primary-500/20'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-primary-500/5 dark:hover:bg-dark-hover hover:text-primary-500 dark:hover:text-primary-400'
                  }`}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-primary-500'
                    }`}
                    aria-hidden="true"
                  />
                  <span className="text-sm font-medium">{item.name}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* User Info and Logout */}
          <div className="p-4 border-t border-light-border dark:border-dark-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-primary-500/10 flex items-center justify-center">
                <span className="text-primary-500 font-medium">
                  {user?.email?.[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 hover:bg-primary-500/5 dark:hover:bg-dark-hover rounded-xl transition-colors duration-200"
            >
              <ArrowLeftOnRectangleIcon className="mr-3 h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {isOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50" onClick={onToggle} />

          {/* Sidebar */}
          <div className="fixed top-8 bottom-0 left-0 w-full max-w-xs bg-light-surface dark:bg-dark-surface">
            <div className="flex flex-col h-full">
              {/* Mobile Header */}
              <div className="p-4 flex justify-between items-center border-b border-light-border dark:border-dark-border">
                <div className="flex items-center gap-3">
                  <img 
                    src="/images/ana_logo.png" 
                    alt="ANA Logo" 
                    className="h-8 w-auto"
                  />
                  <span className="text-xl font-bold text-gray-900 dark:text-white">
                    Admin Panel
                  </span>
                </div>
                <button
                  onClick={onToggle}
                  className="p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 overflow-y-auto p-4">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href || 
                                 (item.href !== '/' && location.pathname.startsWith(item.href));
                  
                  return (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={onToggle}
                      className={`group flex items-center px-4 py-3 mb-2 rounded-xl transition-all duration-200 ${
                        isActive
                          ? 'bg-primary-500/10 dark:bg-primary-500/20 text-primary-500 dark:text-primary-400 border border-primary-500/20'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-primary-500/5 dark:hover:bg-dark-hover hover:text-primary-500 dark:hover:text-primary-400'
                      }`}
                    >
                      <item.icon
                        className={`mr-3 h-5 w-5 flex-shrink-0 ${
                          isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-primary-500'
                        }`}
                        aria-hidden="true"
                      />
                      <span className="text-sm font-medium">{item.name}</span>
                    </NavLink>
                  );
                })}
              </nav>

              {/* Mobile User Info and Logout */}
              <div className="p-4 border-t border-light-border dark:border-dark-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary-500/10 flex items-center justify-center">
                    <span className="text-primary-500 font-medium">
                      {user?.email?.[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    onToggle();
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary-500 dark:hover:text-primary-400 hover:bg-primary-500/5 dark:hover:bg-dark-hover rounded-xl transition-colors duration-200"
                >
                  <ArrowLeftOnRectangleIcon className="mr-3 h-5 w-5" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
